<template>
  <article ref="addMealArticle" class="add-meal-article" @scroll="onScroll">
    <form @submit.prevent @keydown.enter.prevent>
      <input
        name="meal-title"
        :class="{ scrolled }"
        ref="title"
        type="text"
        v-model="meal.title"
        placeholder="Nom du plat"
        @keyup.enter="focusNewIngredient()"
        @blur="onTitleBlur"
      />

      <div class="inputs-container">
        <span
          class="input-line"
          v-for="(ingredient, index) in meal.ingredients"
          :key="index"
        >
          <InputSelect
            class="ingredient"
            v-model="ingredient.title"
            :searchFn="searchIngredients"
            @select="(ingredient) => updateIngredient(index, ingredient)"
          />
          <button class="del-ingredient" @click="removeIngredient(index)">
            <Icon icon="far fa-times-circle" />
          </button>
        </span>
        <span class="new-item-container">
          <label>+</label>
          <InputSelect
            ref="newItem"
            v-model="newItem"
            placeholder="Nouvel ingrédient"
            :searchFn="searchIngredients"
            @select="addIngredient"
          />
        </span>
      </div>
      <div class="images-container" v-show="images.showImagesContainer">
        <div class="search">
          <input
            type="text"
            name="search-image"
            class="search-image"
            placeholder="Recherche d'images"
            v-model="images.search"
            @keyup.enter="searchImages(true)"
          />
          <button class="button-shadow" @click="searchImages(true)">
            Rechercher
          </button>
          <button class="button-shadow del" @click="cancelImages()">
            <Icon icon="fas fa-times" />
          </button>
        </div>
        <div class="images-list">
          <span v-if="currentImg" class="image-container">
            <span
              class="image"
              :class="{ selected: images.selected === -1 }"
              :style="{ backgroundImage: `url(${currentImg})` }"
              @click="images.selected = -1"
            ></span>
            <p>see on <a target="_blank" :href="currentImg">Unsplash</a></p>
          </span>
          <span
            class="image-container"
            v-for="(image, index) in images.list"
            :key="index"
          >
            <span
              class="image"
              :class="{ selected: images.selected === index }"
              :style="{ backgroundImage: `url(${image.url})` }"
              @click="images.selected = index"
            ></span>
            <p>
              see {{ image.author }} on
              <a target="_blank" :href="image.unsplashUrl">Unsplash</a>
            </p>
          </span>
          <p v-if="images.list.length === 0">
            Aucune image ne correspond à votre recherche
          </p>
        </div>
      </div>
      <div class="btn-container">
        <button
          @click="saveMeal()"
          :disabled="meal.title === ''"
          class="btn-save"
        >
          {{ saveBtnLabel }}
        </button>
        <button @click="$emit('close')">Annuler</button>
        <button @click="deleteMeal()" v-if="updateMeal" class="btn-delete">
          Supprimer
        </button>
      </div>
    </form>
  </article>
</template>

<script>
import _ from 'lodash';
import unsplashService from '../../services/unsplash.service';
import mealsService from '../../services/meals.service';
import ingredientsService from '../../services/ingredients.service';
import InputSelect from '../Utils/InputSelect.vue';

export default {
  components: {
    InputSelect,
  },
  props: {
    updateMeal: {
      type: Object,
      default: null,
    },
    onChoose: {
      type: Function,
      default: null,
    },
  },
  data() {
    return {
      meal: {
        title: '',
        ingredients: [],
      },
      images: {
        showImagesContainer: false,
        search: '',
        searchMem: '',
        searchCounter: 0,
        list: [],
        selected: -1,
      },
      newItem: '',
      scrolled: false,
      ingredientsList: [
        { id: 1, title: '1' },
        { id: 2, title: '2' },
      ],
    };
  },
  created() {
    if (this.updateMeal) {
      if (!this.updateMeal.id) {
        throw new Error('update meal needs id');
      }
      this.meal = {
        title: this.updateMeal.title || '',
        ingredients: this.updateMeal.ingredients || [],
      };
      // On affiche les images direct
      this.onTitleBlur();
    }
  },
  mounted() {
    this.$refs.title.focus();
  },
  methods: {
    saveMeal() {
      // image url
      const selectedImg = _.get(
        this.images,
        `list[${this.images.selected}].url`,
        null
      );
      if (selectedImg) {
        this.meal.img = selectedImg;
      }
      if (this.updateMeal) {
        // UPDATE
        mealsService.updateMeal(this.meal, this.updateMeal.id);
      } else {
        // CREATE
        mealsService.addMeal(this.meal, this.currentWorkspaceRef).then((m) => {
          // une fois la création effectuée, on check si callback
          this.meal.id = m.id;
          // Si callback
          if (this.onChoose) {
            this.onChoose(this.meal);
          }
        });
      }
      this.$emit('close');
    },
    deleteMeal() {
      mealsService.deleteMeal(this.updateMeal.id);
      this.$emit('close');
    },
    addIngredient(ingredient, emptyEnter = false) {
      if (emptyEnter) {
        // on a fait entrée alors que le champ était vide, on enregistre
        this.saveMeal();
        return;
      }
      // On utilise l'ingredient en paramètre, sinon les valeurs par défaut
      this.meal.ingredients.push({
        ingredientRef: _.get(ingredient, 'ref', null),
        title: _.get(ingredient, 'title', 'no-title'),
      });
      // Reset new item field
      this.newItem = '';
      this.focusNewIngredient();
    },

    /**
     * Modifie un ingrédient quand on choisit dans la liste
     */
    updateIngredient(index, ingredient) {
      // Seulement si c'est un vrai select, sinon on laisse juste la chaine de char
      if (ingredient) {
        this.meal.ingredients[index] = {
          ingredientRef: ingredient.ref,
          title: ingredient.title,
        };
      }
    },

    /**
     * Suppr d'un ingrédient dans la liste
     */
    removeIngredient(index) {
      this.meal.ingredients.splice(index, 1);
    },
    focusNewIngredient() {
      if (this.$refs.newItem) {
        this.$refs.newItem.focus();
      }
    },
    onBlur(index) {
      if (this.meal.ingredients[index] === '') {
        this.meal.ingredients.splice(index, 1);
      }
    },
    onTitleBlur() {
      const { title } = this.meal;
      if (title.length) {
        this.images.showImagesContainer = true;
        this.images.search = title;
      } else {
        this.images.showImagesContainer = false;
      }
      this.searchImages();
    },
    searchImages(incrementSearch = false) {
      // 5 images, 4 si déjà une en base
      const imagesNb = this.currentImg ? 4 : 5;
      this.images.searchCounter = incrementSearch
        ? this.images.searchCounter + 1
        : 1;
      if (this.images.searchCounter > 6) {
        // Pourquoi 6 ? Au pif.
        this.images.searchCounter = 1;
      }
      const { search } = this.images;
      if (search !== this.images.searchMem) {
        // On a change de termes, on reinit
        this.images.searchCounter = 1;
      }
      this.images.searchMem = search;
      unsplashService
        .searchPhotos(search, this.images.searchCounter, imagesNb)
        .then((res) => {
          this.images.list = res.results.map((photo) => {
            return {
              url: unsplashService.getSmallImage(photo),
              author: unsplashService.getAuthor(photo),
              unsplashUrl: unsplashService.getUnsplashUrl(photo),
            };
          });
        });
    },
    cancelImages() {
      this.images = {
        showImagesContainer: true,
        search: '',
        searchMem: '',
        searchCounter: 0,
        list: [],
        selected: -1,
      };
    },
    onScroll() {
      this.scrolled = this.$(this.$refs.addMealArticle).scrollTop() > 0;
    },
    closeOnEsc() {
      this.$emit('close');
    },

    // Search ingredients
    searchIngredients(text) {
      return ingredientsService.searchIngredients(text);
    },
  },
  computed: {
    saveBtnLabel() {
      return this.updateMeal ? 'Modifier' : 'Ajouter';
    },
    currentImg() {
      // Si c'est en update, on renvoie l'image actuelle
      return _.get(this.updateMeal, 'img', null);
    },
  },
};
</script>

<style lang="scss" src="./MealForm.scss"></style>
